import type { MembershipSummary, PermissionCode, RoleSummary } from '@ldembalagens/types';
import { useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useAuthStore } from '@/stores/auth-store';

// Referências estáveis: evitam re-render infinito com useShallow quando não há sessão.
const EMPTY_ROLES: RoleSummary[] = [];
const EMPTY_MEMBERSHIPS: MembershipSummary[] = [];

export function useAuth() {
  return useAuthStore(
    useShallow((s) => ({
      status: s.status,
      user: s.session?.user ?? null,
      tenant: s.session?.tenant ?? null,
      roles: s.session?.roles ?? EMPTY_ROLES,
      memberships: s.session?.memberships ?? EMPTY_MEMBERSHIPS,
      isSuperAdmin: s.session?.user.isSuperAdmin ?? false,
      isAuthenticated: s.status === 'authenticated',
    })),
  );
}

/** Verifica permissões no tenant ativo. Superadmin sempre tem acesso. */
export function usePermissions() {
  const permissions = useAuthStore((s) => s.permissions);
  const isSuperAdmin = useAuthStore((s) => s.session?.user.isSuperAdmin ?? false);
  const hasTenant = useAuthStore((s) => Boolean(s.session?.tenant));

  const can = useCallback(
    (...required: PermissionCode[]) => {
      if (required.length === 0) return true;
      if (!hasTenant) return false;
      if (isSuperAdmin) return true;
      return required.every((p) => permissions.has(p));
    },
    [permissions, isSuperAdmin, hasTenant],
  );

  const canAny = useCallback(
    (...required: PermissionCode[]) => {
      if (required.length === 0) return true;
      if (!hasTenant) return false;
      if (isSuperAdmin) return true;
      return required.some((p) => permissions.has(p));
    },
    [permissions, isSuperAdmin, hasTenant],
  );

  return { can, canAny, isSuperAdmin, hasTenant };
}
